class DocumentDownloadService {
    constructor(toastRef = null) {
        this.toastRef = toastRef;
    }

    /**
     * Toast mesajı gösterir
     * @param {string} severity - success, error, warn, info
     * @param {string} summary - Başlık
     * @param {string} detail - Detay mesajı
     * @param {number} life - Gösterim süresi (ms)
     */
    showToast(severity, summary, detail, life = 3000) {
        if (this.toastRef && this.toastRef.current) {
            this.toastRef.current.show({
                severity,
                summary,
                detail,
                life
            });
        }
    }

    /**
     * Base64 içeriği dosya olarak indirir
     * @param {string} base64Content - Base64 formatındaki dosya içeriği
     * @param {string} fileName - İndirilecek dosya adı
     * @param {string} mimeType - Dosya MIME tipi
     * @param {boolean} showSuccessToast - Başarı toast'ı gösterilsin mi
     */
    downloadBase64File(base64Content, fileName, mimeType = 'application/octet-stream', showSuccessToast = true) {
        if (!base64Content) {
            throw new Error("İndirilecek dosya içeriği bulunamadı (base64 data eksik).");
        }

        const dataUrl = `data:${mimeType};base64,${base64Content}`;
        const link = document.createElement('a');

        try {
            link.href = dataUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Toast mesajını sadece istendiğinde göster
            if (showSuccessToast) {
                this.showToast('success', 'Başarılı', 'Dosya başarıyla indirildi.');
            }
        } catch (error) {
            if (document.body.contains(link)) {
                document.body.removeChild(link);
            }
            throw error;
        }
    }

    /**
     * Blob içeriği dosya olarak indirir
     * @param {Blob} blob - Blob formatındaki dosya içeriği
     * @param {string} fileName - İndirilecek dosya adı
     * @param {boolean} showSuccessToast - Başarı toast'ı gösterilsin mi
     */
    downloadBlob(blob, fileName, showSuccessToast = true) {
        if (!blob) {
            throw new Error("İndirilecek dosya içeriği bulunamadı (blob data eksik).");
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        try {
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            // Toast mesajını sadece istendiğinde göster
            if (showSuccessToast) {
                this.showToast('success', 'Başarılı', 'Dosya başarıyla indirildi.');
            }
        } catch (error) {
            if (document.body.contains(link)) {
                document.body.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
            throw error;
        }
    }

    /**
     * API yanıtından dosya adını çıkarır
     * @param {string} apiFileName - API'den gelen dosya adı
     * @param {string} fallbackFileName - Yedek dosya adı
     * @returns {string} Temizlenmiş dosya adı
     */
    extractFileName(apiFileName, fallbackFileName = 'downloaded_file') {
        if (apiFileName && typeof apiFileName === 'string') {
            return apiFileName.split('/').pop() || fallbackFileName;
        }
        return fallbackFileName;
    }

    /**
     * API yanıtından hata mesajını çıkarır
     * @param {Object} error - Hata objesi
     * @returns {string} Hata mesajı
     */
    extractErrorMessage(error) {
        if (error?.data?.message) {
            return error.data.message;
        }
        if (error?.error) {
            return error.error;
        }
        if (error?.message) {
            return error.message;
        }
        if (error?.status) {
            return `HTTP Hatası: ${error.status}`;
        }
        return 'Bilinmeyen hata oluştu.';
    }

    /**
     * Generic dosya indirme işlemi - API trigger fonksiyonu kullanarak
     * @param {Function} triggerFunction - RTK Query lazy hook trigger fonksiyonu
     * @param {any} queryParam - Sorgu parametresi (fileKey, id, vb.)
     * @param {Object} documentData - Dosya metadata bilgileri
     * @param {Object} options - Opsiyonlar
     * @param {string} options.responseType - 'base64' | 'blob' | 'json'
     * @param {Function} options.dataExtractor - Yanıttan veri çıkarma fonksiyonu
     * @param {Function} options.fileNameExtractor - Dosya adı çıkarma fonksiyonu
     * @param {Function} options.mimeTypeExtractor - MIME tipi çıkarma fonksiyonu
     * @param {boolean} options.showSuccessToast - Başarı toast'ı gösterilsin mi
     */
    async downloadWithTrigger(
        triggerFunction,
        queryParam,
        documentData,
        options = {}
    ) {
        const {
            responseType = 'json',
            dataExtractor = (response) => response.data,
            fileNameExtractor = (response, docData) =>
                this.extractFileName(response.data?.fileName, docData?.fileName),
            mimeTypeExtractor = (response) =>
                response.data?.contentType || 'application/octet-stream',
            showSuccessToast = false // Varsayılan olarak false yap - sadece en üst seviyede göster
        } = options;

        try {
            const response = await triggerFunction(queryParam);

            if (response.error) {
                const errorMessage = this.extractErrorMessage(response.error);
                throw new Error(errorMessage);
            }

            const data = dataExtractor(response);
            const fileName = fileNameExtractor(response, documentData);
            const mimeType = mimeTypeExtractor(response);

            if (responseType === 'base64') {
                if (!data.content) {
                    throw new Error("İndirilecek dosya içeriği bulunamadı (base64 data eksik).");
                }
                this.downloadBase64File(data.content, fileName, mimeType, showSuccessToast);
            } else if (responseType === 'blob') {
                this.downloadBlob(data, fileName, showSuccessToast);
            } else if (responseType === 'json') {
                if (!data.content) {
                    throw new Error("İndirilecek dosya içeriği bulunamadı (content eksik).");
                }
                this.downloadBase64File(data.content, fileName, mimeType, showSuccessToast);
            }

        } catch (error) {
            console.error("Dosya indirme işlemi başarısız oldu:", error);
            this.showToast(
                'error',
                'İndirme Hatası',
                `Dosya indirilemedi. Detay: ${error.message}`,
                4000
            );
            throw error;
        }
    }

    /**
     * Önceden tanımlanmış base64 JSON yanıt formatı için hızlı indirme
     * @param {Function} triggerFunction - RTK Query lazy hook trigger fonksiyonu
     * @param {string} fileKey - Dosya anahtarı
     * @param {Object} documentData - Dosya metadata bilgileri
     * @param {Object} messages - Özel mesajlar
     */
    async downloadByKey(triggerFunction, fileKey, documentData, messages = {}) {
        if (!fileKey) {
            throw new Error("Dosya anahtarı bulunamadı.");
        }

        try {
            await this.downloadWithTrigger(
                triggerFunction,
                fileKey,
                documentData,
                {
                    responseType: 'json',
                    dataExtractor: (response) => response.data.data,
                    fileNameExtractor: (response, docData) =>
                        this.extractFileName(response.data?.data?.fileName, docData?.fileName),
                    mimeTypeExtractor: (response) =>
                        response.data?.data?.contentType || 'application/octet-stream',
                    showSuccessToast: false // Burada gösterme
                }
            );

            // Özel başarı mesajı varsa kullan, yoksa varsayılan mesajı göster
            const successMessage = messages.successMessage || 'Dosya başarıyla indirildi.';
            this.showToast('success', 'Başarılı', successMessage);
        } catch (error) {
            // Özel hata mesajı varsa kullan
            const errorMessage = messages.errorMessage || `Dosya indirilemedi. Detay: ${error.message}`;
            this.showToast('error', 'İndirme Hatası', errorMessage, 4000);
            throw error;
        }
    }

    /**
     * Blob yanıt formatı için hızlı indirme
     * @param {Function} triggerFunction - RTK Query lazy hook trigger fonksiyonu
     * @param {any} queryParam - Sorgu parametresi
     * @param {Object} documentData - Dosya metadata bilgileri
     */
    async downloadAsBlob(triggerFunction, queryParam, documentData) {
        await this.downloadWithTrigger(
            triggerFunction,
            queryParam,
            documentData,
            {
                responseType: 'blob',
                dataExtractor: (response) => response.data,
                fileNameExtractor: (response, docData) => docData?.fileName || 'downloaded_file',
                showSuccessToast: true // Blob indirme için direkt toast göster
            }
        );
    }

    /**
     * Dosya güncelleme işlemi için yardımcı fonksiyon
     * @param {Object} documentData - Güncellenecek dosya bilgileri
     * @param {Function} onUpdateCallback - Güncelleme callback fonksiyonu
     */
    async updateDocument(documentData, onUpdateCallback) {
        try {
            if (typeof onUpdateCallback === 'function') {
                await onUpdateCallback(documentData);
                this.showToast('success', 'Başarılı', 'Dosya başarıyla güncellendi.');
            } else {
                throw new Error('Güncelleme callback fonksiyonu bulunamadı.');
            }
        } catch (error) {
            console.error("Dosya güncelleme işlemi başarısız oldu:", error);
            this.showToast(
                'error',
                'Güncelleme Hatası',
                `Dosya güncellenemedi. Detay: ${error.message}`,
                4000
            );
            throw error;
        }
    }

    /**
     * Dosya silme işlemi için yardımcı fonksiyon
     * @param {Object} documentData - Silinecek dosya bilgileri
     * @param {Function} onDeleteCallback - Silme callback fonksiyonu
     * @param {Object} options - Opsiyonlar
     * @param {boolean} options.confirmDialog - Onay dialog'u gösterilsin mi
     */
    async deleteDocument(documentData, onDeleteCallback, options = {}) {
        const { confirmDialog = true } = options;

        try {
            if (confirmDialog) {
                const confirmed = window.confirm(`'${documentData.fileName}' dosyasını silmek istediğinizden emin misiniz?`);
                if (!confirmed) {
                    return;
                }
            }

            if (typeof onDeleteCallback === 'function') {
                await onDeleteCallback(documentData);
                this.showToast('success', 'Başarılı', 'Dosya başarıyla silindi.');
            } else {
                throw new Error('Silme callback fonksiyonu bulunamadı.');
            }
        } catch (error) {
            console.error("Dosya silme işlemi başarısız oldu:", error);
            this.showToast(
                'error',
                'Silme Hatası',
                `Dosya silinemedi. Detay: ${error.message}`,
                4000
            );
            throw error;
        }
    }
}

export default DocumentDownloadService;